import { readActivationContext, recordSoftphoneDiagnostic } from './diagnostics'

/**
 * The `<audio>` element the caller's audio is played through.
 *
 * WebRTC only delivers the far end as a `MediaStream`; nothing is audible until that stream is
 * attached to a media element. The Telnyx SDK does that attach for us, but ONLY into the element
 * it was handed: on the peer connection's `track` event it runs `attachMediaStream(remoteElement,
 * remoteStream)`, which resolves the element and returns silently when it is null. It never
 * creates one (the single `createElement('audio')` inside the SDK is for its own ringtone /
 * ringback files). With no element the inbound RTP lands in `call.options.remoteStream` and stops
 * there - the agent hears nothing while the caller hears the agent, because the microphone track
 * needs no element to be sent.
 *
 * Gotcha worth keeping: `new TelnyxRTC({ remoteElement })` does NOT work. The client constructor
 * hard-assigns `_remoteElement = null` and only reads `video`, `iceServers`, `ringtoneFile` and
 * `ringbackFile` off its options. The element has to go through the `remoteElement` setter (or
 * per-call, through `call.answer({ remoteElement })`).
 */
export const REMOTE_AUDIO_ELEMENT_ID = 'softphone-remote-audio'

/**
 * Returns the singleton remote audio element, creating and appending it on first use. `autoplay`
 * and `playsinline` are what actually start playback once the SDK sets `srcObject`; the SDK sets
 * both itself when attaching, they are set here too so the element is never briefly silent.
 */
export function getRemoteAudioElement() {
  if (typeof document === 'undefined') {
    return null
  }

  const existing = document.getElementById(REMOTE_AUDIO_ELEMENT_ID)

  if (existing) {
    return existing
  }

  const element = document.createElement('audio')

  element.id = REMOTE_AUDIO_ELEMENT_ID
  element.autoplay = true
  element.setAttribute('autoplay', 'autoplay')
  element.setAttribute('playsinline', 'playsinline')
  element.style.display = 'none'

  document.body.appendChild(element)

  return element
}

/**
 * Autoplay is only a policy problem when no user gesture preceded playback. Going online and
 * answering are both clicks, so the `autoplay` attribute is normally enough - this is the
 * defensive second attempt for the cases where it is not, and a rejected promise is not an error
 * worth surfacing to the agent (the call is up either way).
 */
export function playRemoteAudio(element) {
  if (!element || typeof element.play !== 'function') {
    return Promise.resolve()
  }

  // Read before `play()`: user activation can expire before the promise settles.
  const context = readActivationContext()

  return Promise.resolve(element.play()).then(() => {
    recordSoftphoneDiagnostic('media', 'remote_audio_play', { result: 'started', error: null, ...context })
  }, error => {
    recordSoftphoneDiagnostic('media', 'remote_audio_play', { result: 'refused', error: error?.name || null, ...context })
  })
}

/**
 * Drops the element and anything still attached to it. Called on disconnect so a later Go Live
 * starts from a clean element rather than one holding a dead stream.
 */
export function releaseRemoteAudioElement() {
  if (typeof document === 'undefined') {
    return
  }

  const element = document.getElementById(REMOTE_AUDIO_ELEMENT_ID)

  if (!element) {
    return
  }

  if (typeof element.pause === 'function') {
    element.pause()
  }

  // The SDK stops the remote tracks when a call finalises, but a disconnect that threw never gets
  // that far. Stopping them here is what makes the element safe to drop: an inbound track left
  // live would keep the old call's audio flowing into an element nobody owns any more.
  const stream = element.srcObject

  if (stream && typeof stream.getTracks === 'function') {
    stream.getTracks().forEach(track => {
      if (typeof track.stop === 'function') {
        track.stop()
      }
    })
  }

  element.srcObject = null
  element.remove()
}
